// providers/ValidationProvider.tsx

import { ReactNode, useMemo } from "react";

// ... import semua tipe data dan utilitas validasi yang Anda butuhkan
import { useCheckingProfile, ValidationItem } from "@/api/master-profile";

import { ValidationContext } from "../contexts/validationContext";
import {
  AllDataSources,
  dataMappers,
  defaultMapper,
} from "../utils/dataMappers";
import { defaultValidator, validationStrategies } from "../utils/validations";

// Tipe untuk status keseluruhan
export type OverallStatus =
  | "Unvalidated"
  | "Validated"
  | "Partially Validated"
  | "Validation Bypassed"
  | "Optional"
  | "Loading";

// Tipe untuk hasil validasi yang sudah dihitung
export interface ValidationResult extends ValidationItem {
  isValid: boolean | null;
}

// Helper function untuk mengecek kondisi bypassed dan optional
function checkBypassedAndOptionalStatus(
  isRequiredRule: ValidationItem | undefined,
  otherRules: ValidationItem[],
  dataSources: AllDataSources,
): OverallStatus | null {
  if (!isRequiredRule) return null;

  if (
    isRequiredRule.isValidateNow === null &&
    otherRules.every((r: ValidationItem) => r.isValidateNow === null)
  ) {
    return "Optional";
  }

  if (
    typeof isRequiredRule.isValidateNow === "boolean" &&
    otherRules.every((r: ValidationItem) => r.isValidateNow === null)
  ) {
    return "Validation Bypassed";
  }

  const isSubmitted =
    (dataSources.opmFdts?.length &&
      dataSources.opmFdts?.every(
        (fdt) =>
          fdt.imageFdtStatus === true ||
          fdt.poleFdtStatus === true ||
          fdt.inputFdtStatus === true ||
          fdt.outputFdtStatus === true,
      )) ||
    (dataSources.opmFats?.length &&
      dataSources.opmFats?.every(
        (fat) =>
          fat.imageFatStatus === true ||
          fat.poleFatStatus === true ||
          fat.outputFatStatus === true,
      )) ||
    (dataSources.opmPoles?.length &&
      dataSources.opmPoles?.every((pole) => pole.poleIdStatus === true));

  if (!isSubmitted) return "Unvalidated"; // Jika data belum pernah disubmit, tetap dianggap Unvalidated

  return null;
}

// Helper function untuk menghitung status validasi normal
function calculateValidationStatus(
  processedValidations: ValidationResult[],
): OverallStatus {
  const validateNowItems = processedValidations.filter(
    (v) => v.isValidateNow && v.code !== "A00",
  );
  const validateLaterItems = processedValidations.filter(
    (v) => !v.isValidateNow && v.isValidateNow !== null && v.code !== "A00",
  );

  const allNowItemsValid = validateNowItems.every((item) => item.isValid);
  const allLaterItemsValid = validateLaterItems.every((item) => item.isValid);
  const isPartiallyValidated = allNowItemsValid && !allLaterItemsValid;

  if (!allNowItemsValid) {
    return "Unvalidated";
  } else if (isPartiallyValidated) {
    return "Partially Validated";
  } else if (allNowItemsValid && allLaterItemsValid) {
    return "Validated";
  } else {
    // Fallback jika tidak ada item sama sekali
    return "Unvalidated";
  }
}

// Menghitung ulang overall status dari kalkulasi murni yang sama, tanpa
// lewat useMemo/context yang bisa masih menunggu re-render. Dipakai tepat
// setelah submit sukses supaya tidak perlu menunggu refetch (menghindari
// race condition overallStatus stale).
function computeStatusFromSources(
  allValidations: ValidationItem[] | undefined,
  isLoading: boolean,
  dataSources: AllDataSources,
) {
  if (!allValidations) {
    return {
      requiredList: [] as ValidationResult[],
      unrequiredList: [] as ValidationResult[],
      validationResults: {},
      overallStatus: (isLoading ? "Loading" : "Optional") as OverallStatus,
    };
  }

  const validationResults: Record<
    string,
    { isValid: boolean | null; isValidateNow: boolean | null }
  > = {};

  const processedValidations: ValidationResult[] = allValidations.map(
    (item: ValidationItem) => {
      const validator = validationStrategies[item.code] ?? defaultValidator;
      const getData = dataMappers[item.code] ?? defaultMapper;
      const requiredData = getData(dataSources);
      const isValid = validator ? validator(requiredData) : false;

      validationResults[item.code] = {
        isValid: item.code === "A00" ? null : isValid,
        isValidateNow: item.isValidateNow,
      };

      return { ...item, isValid };
    },
  );

  const overallStatus = calculateOverallStatus(
    allValidations,
    processedValidations,
    dataSources,
  );

  const requiredList = processedValidations.filter(
    (item) =>
      item.isValidateNow && item.isValidateNow !== null && item.code !== "A00",
  );
  const unrequiredList = processedValidations.filter(
    (item) =>
      !item.isValidateNow && item.isValidateNow !== null && item.code !== "A00",
  );

  return { requiredList, unrequiredList, validationResults, overallStatus };
}

// Main function untuk menghitung overall status
function calculateOverallStatus(
  allValidations: ValidationItem[],
  processedValidations: ValidationResult[],
  dataSources: AllDataSources,
): OverallStatus {
  const isRequiredRule = allValidations.find(
    (v: ValidationItem) => v.code === "A00",
  );
  const otherRules = allValidations.filter(
    (v: ValidationItem) => v.code !== "A00",
  );

  // Cek kondisi bypassed & optional terlebih dahulu
  const specialStatus = checkBypassedAndOptionalStatus(
    isRequiredRule,
    otherRules,
    dataSources,
  );

  if (specialStatus) {
    return specialStatus;
  }

  // Jika bukan bypassed atau optional, hitung status normal
  return calculateValidationStatus(processedValidations);
}

// Provider akan menerima data yang dibutuhkan untuk validasi sebagai props
export function ValidationProvider({
  children,
  checkingProfileId,
  dataSources,
}: {
  children: ReactNode;
  checkingProfileId: string | undefined;
  dataSources: AllDataSources;
}) {
  const { data: checkingProfileData, isLoading } = useCheckingProfile(
    { id: checkingProfileId ?? "" },
    { enabled: !!checkingProfileId },
  );

  // LOGIKA DARI OPMCheckingProfile PINDAH KE SINI
  const validationSummary = useMemo(() => {
    if (!checkingProfileData?.validations?.OPM) {
      return {
        requiredList: [],
        unrequiredList: [],
        validationResults: {},
        overallStatus: (isLoading ? "Loading" : "Optional") as OverallStatus,
      };
    }

    return computeStatusFromSources(
      checkingProfileData.validations.OPM,
      isLoading,
      dataSources,
    );
  }, [checkingProfileData, isLoading, dataSources]);

  // Menghitung ulang status di call-time (bukan dari value ter-memo), pakai
  // dataSources yang di-override. Dipanggil langsung dari onSuccess submit,
  // tanpa menunggu refetch/re-render.
  function getStatusForSubmit(overrideDataSources: Partial<AllDataSources>) {
    return computeStatusFromSources(
      checkingProfileData?.validations?.OPM,
      isLoading,
      { ...dataSources, ...overrideDataSources },
    );
  }

  const value = { ...validationSummary, isLoading, getStatusForSubmit };

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
}
